import { useEffect, useState, useRef, useCallback } from 'react';
import { AddonsService, IAddon } from '../services/AddonsService';
import i18n from '../plugins/i18n/i18n';
import { useApi } from '../services/hooks/apiRedux';
import { useAuth } from '../store/auth';
import { errorToast, successToast } from '../utils/toasts';
import { InstancesServices } from '../services/InstancesServices';
import { GetAddonsResponse, UpdateAddonsPayload } from '../services/Interfaces/IAccountService';

export const useAddons = () => {
  const [addons, setAddons] = useState<GetAddonsResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const api = useApi();
  const { token } = useAuth();
  const initialLoadRef = useRef(true);

  const fetchAddons = useCallback(async () => {
    setLoading(true);

    try {
      if (!token) {
        errorToast(i18n.t('environment.userNotAuthenticated'));
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Language': i18n.language.toLowerCase()
        }
      };

      const response = await InstancesServices.getAddons(api, config);
      setAddons(response);


    } catch (error: any) {
      errorToast(error?.response?.data?.message || i18n.t('environment.errorFetchingAddons'));
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const updateAddons = async (payload: UpdateAddonsPayload[]) => {
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Language': i18n.language.toLowerCase()
        }
      };

      await InstancesServices.updateAddons(api, payload, config);
      await fetchAddons();
     
      successToast(i18n.t('environment.addonsUpdatedSuccess'));
    } catch (error: any) {
      console.error('Erro ao atualizar taxa:', error?.response?.data?.error);
      errorToast(error?.response?.data?.error?.[0] || i18n.t('environment.errorUpdatingFee'));
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    // Primeira carga - não precisa de delay
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      void fetchAddons();
      return;
    }

    // Quando o idioma mudar, espera um pouco antes de refazer a requisição
    const timeout = setTimeout(() => {
      void fetchAddons();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [fetchAddons, i18n.language]);

  return { addons, loading, updateAddons };
};
